import React from "react";
import FeedListV2 from "AppComponents/FeedListv2";
import _ from "lodash";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import { graphql } from "lib/apolloClient";
import { getInteractionListQuery } from "MessagingV2/modules/MessagingQuery";
import { getOrganizationId } from "modules/CommonSelectors";
import useI18n from "Hooks/UseI18n";
import {
  getFormattedNameForUserType,
  INTERACTION_TYPE,
} from "MessagingV2/modules/Utils";
import { useDispatch } from "Hooks/useDispatch";
import { useHandleScroll } from "Utils/CustomHooks";

import Actions from "./Actions";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { EmptyState, Tooltip } from "@toddle-edu/ds-web";
import { goToMessagingV2FormTemplateRoute } from "MessagingV2/modules/MessagingModule";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { NoAnnouncementSearchIllustration } from "@toddle-edu/ds-theme";
import { FORM_TEMPLATE_TABS } from "MessagingV2/routes/FormTemplates/modules/utils";

import classes from "./FormTemplates.scss";

const columnList = [
  {
    label: "Template Name",
    locale: "common:template_name",
    style: { width: "34%", maxWidth: "34%" },
    id: "templateName",
    type: "CUSTOM",
  },
  {
    label: "No. of Questions",
    locale: "common:no_of_questions",
    id: "totalQuestions",
    style: { width: "33%", maxWidth: "33%" },
    type: "CUSTOM",
  },
  {
    label: "Creator",
    locale: "common:creator",
    id: "creator",
    style: { width: "33%", maxWidth: "33%" },
    type: "CUSTOM",
  },
  {
    label: "Actions",
    locale: "common:action_other",
    id: "action",
    type: "CUSTOM",
  },
];

const CustomText = ({ text }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const checkIfTextOverflow = React.useCallback(node => {
    if (node) {
      if (node.offsetWidth < node?.scrollWidth) {
        setShowTooltip(true);
      } else {
        setShowTooltip(false);
      }
    }
  }, []);

  return (
    <ConditionalWrapper
      wrapper={<Tooltip tooltip={text} />}
      condition={showTooltip}
      type={"wrapperAsElement"}
    >
      <div ref={checkIfTextOverflow} className={"text-body"}>
        {text}
      </div>
    </ConditionalWrapper>
  );
};

const getCustomComponent = ({ params, queryVariables }) => {
  const id = _.get(params, "column.id");
  switch (id) {
    case "templateName": {
      const data = _.get(params, "columnData", "");
      return <CustomText text={data} />;
    }
    case "totalQuestions": {
      const data = _.get(params, "columnData", "");
      return <CustomText text={data} />;
    }
    case "creator": {
      const data = _.get(params, "columnData", "");
      return <CustomText text={data} />;
    }
    case "action": {
      const templateId = _.get(params, "id");

      const data = _.get(params, "columnData", {});
      const createdBy = _.get(data, "createdBy", {});

      return (
        <Actions
          templateId={templateId}
          queryVariables={queryVariables}
          createdBy={createdBy}
        />
      );
    }
    default:
      return null;
  }
};

const FormTemplates = props => {
  const {
    templateEdges,
    isLoading,
    isData,
    queryVariables,
    containerRef,
    networkStatus,
    hasNextPage,
    loadMoreStaff,
  } = props;
  const dispatch = useDispatch();
  const { chatId } = useChatOptions();
  const { t } = useI18n(["common"]);

  const feedData = React.useMemo(() => {
    return _.map(templateEdges, edge => {
      const totalQuestions = _.get(edge, "totalQuestions", 0);
      const id = _.get(edge, "node.id", "");
      const templateName = _.get(edge, "node.title", "");
      const user = _.get(edge, "node.createdBy", {});
      const creator = getFormattedNameForUserType({
        user,
      });
      return {
        id,
        templateName,
        totalQuestions,
        creator,
        action: {
          createdBy: user,
        },
      };
    });
  }, [templateEdges]);

  const onRowClick = React.useCallback(
    templateId =>
      dispatch(
        goToMessagingV2FormTemplateRoute({
          chatId,
          templateId,
          tabKey: FORM_TEMPLATE_TABS.PREVIEW,
        })
      ),
    [dispatch, chatId]
  );

  const getFeedCustomComponent = React.useCallback(
    params => getCustomComponent({ queryVariables, params }),
    [queryVariables]
  );

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreStaff,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  // empty state
  if (_.isEmpty(feedData) && isData && !isLoading) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyState}>
          <EmptyState
            dsVersion="2.0"
            illustration={NoAnnouncementSearchIllustration}
            title={t("common:no_template_found")}
            subtitle={t("common:empty_form_template_subtext")}
          />
        </div>
      </div>
    );
  }

  return (
    <FeedListV2
      isLoading={isLoading}
      isData={isData}
      columnList={columnList}
      feedData={feedData}
      getCustomComponent={getFeedCustomComponent}
      enableStickyHeader
      dsVersion="2.0"
      onItemClick={onRowClick}
    />
  );
};

export default graphql(getInteractionListQuery, {
  name: "getTemplate",
  options: ({ selectedUsers, searchText }) => {
    return {
      fetchPolicy: "cache-and-network",
      variables: {
        id: getOrganizationId(),
        input: {
          isTemplate: true,
          createdBy: selectedUsers,
          searchText,
          first: 20,
          type: INTERACTION_TYPE.FORM,
        },
      },
    };
  },
  props({ getTemplate }) {
    const queryVariables = _.get(getTemplate, "variables", {});

    const node = _.get(getTemplate, "node", {});
    const pageInfo = _.get(node, "messageV2Interactions.pageInfo", {});
    const hasNextPage = _.get(
      node,
      "messageV2Interactions.pageInfo.hasNextPage",
      false
    );
    const templateEdges = _.get(node, "messageV2Interactions.edges", []);

    return {
      isLoading: _.includes([1, 2, 3], getTemplate["networkStatus"]),
      isData: !_.isEmpty(node),
      templateEdges,
      hasNextPage,
      queryVariables,
      networkStatus: getTemplate["networkStatus"],
      loadMoreStaff: prepareLoadMoreFeed({
        variables: {
          ...queryVariables,
          input: {
            ...queryVariables.input,
            after: _.get(pageInfo, "endCursor", undefined),
          },
        },
        queryData: getTemplate,
        key: "messageV2Interactions",
      }),
    };
  },
})(FormTemplates);
